import React, { useState, useEffect } from 'react';
import { Paper, Box, Typography, Select, MenuItem, Switch, Tooltip, FormControlLabel, FormGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import './metrics_below_map.css';

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(() => ({
  '& .MuiTooltip-tooltip': {
    backgroundColor: '#555',
    color: '#fff',
    fontSize: '0.8rem',
    borderRadius: '4px',
    padding: '6px',
  },
  '& .MuiTooltip-arrow': {
    color: '#555',
  },
}));

const MetricsBelowMap = ({ metricsNames, cqlFecha, weekNumber, issueTime, cqlSevere, handleRiskClick, onMenuOpen,   // <--- AÑADE ESTO
  onMenuClose }) => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useN1M, setUseN1M] = useState(false);
  const [selectedSevere, setSelectedSevere] = useState('Low');


  const removeN1mSuffix = (data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => {
        const newKey = key.endsWith('_n1m') ? key.slice(0, -4) : key;
        return [newKey, value];
      })
    );
  };

  const formatPercentage = (value) => {
    if (typeof value === 'number') {
      return `${(value * 1).toFixed(3)}`;
    }
    return value;
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    const endpoint = useN1M
      ? '/api/get_metrics_below_map_n1m'
      : '/api/get_metrics_below_map';
    const url = `${process.env.REACT_APP_API_URL}${endpoint}?cqlFecha=${encodeURIComponent(cqlFecha)}&cqlSevere=${encodeURIComponent(selectedSevere)}&weekNumber=${encodeURIComponent(weekNumber)}&metricsNames=${encodeURIComponent(metricsNames)}&issueTime=${encodeURIComponent(issueTime)}`;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('Data:', data);
      const processedData = useN1M
        ? data.map((item) => removeN1mSuffix(item))
        : data;
      setMetrics(processedData);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (cqlFecha && selectedSevere && weekNumber && metricsNames && issueTime) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [metricsNames, cqlFecha, selectedSevere, weekNumber, issueTime, useN1M]);

  if (error) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="error" align="center">
          Error loading metrics.
        </Typography>
      </Box>
    );
  }

  const metricsData = Array.isArray(metrics) && metrics.length > 0 ? metrics[0] : {};

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',

        bgcolor: 'transparent',
        p: '4px 0px',
        borderRadius: '0px',
        border: 'none',
        backdropFilter: 'none',

        width: 'max-content',
        mx: 'auto',
        color: 'white',
        gap: 2,
        overflow: 'visible',

        // Quitamos las transiciones de escala de aquí, 
        // porque ahora es el Box Padre el que se hace grande (scale 1.1)
        transition: 'none',
        '&:hover': {
          transform: 'none',
        },
        '&:focus-within': {
          transform: 'none',
        }
      }}
    >
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        flexWrap: 'nowrap',
        minWidth: 'max-content',
        pb: 1
      }}>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                checked={useN1M}
                onChange={() => setUseN1M(u => !u)}
                size="small"
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'inline-flex', alignItems: 'center', fontSize: '0.80rem', fontWeight: 400 }}>
                Show Neighbourhood Metrics
                <CustomTooltip
                  title={
                    <span style={{
                      display: 'block',
                      width: '100%',
                      maxWidth: '600px',
                      padding: '12px',
                      borderRadius: '4px',
                      fontSize: 'clamp(0.68rem, 1vw, 0.68rem)',
                      lineHeight: 1.6,
                      boxSizing: 'border-box',
                      textAlign: 'left'
                    }}>
                      <strong>Neighborhood Metrics</strong><br />
                      <p>
                        Include surrounding grid points to allow for small spatial shifts, giving credit when forecasts are slightly offset but still operationally useful.
                      </p>
                      <ul>
                        <li><strong>Precision</strong>: The proportion of predicted convection cases that were actually observed.</li>
                        <li><strong>Recall (Probability of Detection)</strong>: The proportion of observed convection cases that were correctly forecasted.</li>
                        <li><strong>F1 Score</strong>: The harmonic mean of precision and recall — a balanced measure that accounts for both missed events and false alarms.</li>
                        <li><strong>False Alarm Ratio (FAR)</strong>: The proportion of predicted convection events that did not occur.</li>
                        <li><strong>Gilbert Skill Score (GSS)</strong>: Adjusts for correct forecasts that could occur by chance, providing a fairer measure of skill for rare events.</li>
                      </ul>
                    </span>
                  }
                  arrow
                  placement="right"
                  enterTouchDelay={0}
                  leaveTouchDelay={3000}
                >
                  <InfoOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: 'gray',
                      ml: 0.5,
                      cursor: 'pointer',
                    }}
                  />
                </CustomTooltip>
              </Box>
            }
          />
        </FormGroup>


        <Box
          sx={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 1,
            alignItems: 'center',
            ml: 2
          }}
        >
          {Object.entries(metricsData).map(([label, value]) => (
            <Box
              key={label}
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1,
                py: 0.5,
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(5px)',
                width: '115px',           
                
              }}
            >
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', mr: 0.5, fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                {label}:
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: 'white', fontSize: '0.7rem' }}>
                {formatPercentage(value)}
              </Typography>
            </Box>
          ))}
        </Box>
        <Select
          onOpen={onMenuOpen} // IMPORTANTE: Avisa al padre que se abra
          onClose={onMenuClose}
          value={selectedSevere}
          onChange={e => { setSelectedSevere(e.target.value); handleRiskClick(e.target.value) }}
          sx={{
            fontSize: '0.9rem',
            fontWeight: 'bold',
            color: 'white',
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderRadius: '4px',
            '& .MuiSelect-select': {
              padding: '4px 24px 4px 8px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: '1px solid rgba(255, 255, 255, 0.2)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.4)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white',
            },
            '& .MuiSvgIcon-root': {
              color: 'white',
            },
            minWidth: 100,
            flexShrink: 0
          }}
          MenuProps={{


            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'bottom', horizontal: 'left' },
            PaperProps: {
              sx: {

                bgcolor: '#333',
                color: 'white',
                boxShadow: '0 -5px 20px rgba(0, 0, 0, 0.8)',
                marginTop: '-10px',
                zIndex: 9999,

                '& .MuiMenuItem-root': {
                  fontSize: '0.9rem',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.1)'
                  }
                }
              }
            }
          }}
        >
          <MenuItem value="Low">Low</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="High">High</MenuItem>

        </Select>
      </Box>

      {/* {loading && (
        <Typography variant="body2" align="center" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.8)' }}>
          Updating...
        </Typography>
      )} */}
    </Box >
  );
};

export default MetricsBelowMap;
